import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Wallet } from "lucide-react";

const MODES = {
  macro: {
    key: "macro",
    label: "Macro Mode",
    icon: Flame,
    accent: "#10B981",
    statLabel: "Energy consumed today",
    stat: "1,740",
    unit: "kcal",
    goal: "of 2,200 kcal goal",
    days: [62, 48, 84, 40, 71, 56, 66],
  },
  capital: {
    key: "capital",
    label: "Capital Mode",
    icon: Wallet,
    accent: "#0EA5E9",
    statLabel: "Capital spent today",
    stat: "$96",
    unit: "USD",
    goal: "of $150 daily budget",
    days: [30, 55, 22, 78, 36, 24, 48],
  },
};

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

export default function AppPreview() {
  const [mode, setMode] = useState("macro");
  const m = MODES[mode];

  return (
    <section id="preview" className="relative py-24 sm:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(16,185,129,0.06),transparent_60%)]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-emerald-400/90">
            Live Preview
          </p>
          <h2 className="text-2xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            One switch. Two worlds.
          </h2>
          <p className="mt-4 text-lg text-neutral-400">
            Flip between your metabolic ledger and your money ledger.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="mx-auto mt-14 max-w-3xl"
        >
          <div className="mb-8 flex justify-center">
            <div className="flex rounded-full border border-white/10 bg-[#111318] p-1">
              {Object.values(MODES).map((opt) => (
                <button
                  key={opt.key}
                  data-testid={opt.key === "macro" ? "app-mode-macro-tab" : "app-mode-capital-tab"}
                  onClick={() => setMode(opt.key)}
                  className={`relative flex items-center gap-2 rounded-full px-6 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors duration-300 ${
                    mode === opt.key ? "text-emerald-950" : "text-neutral-400 hover:text-slate-100"
                  }`}
                >
                  {mode === opt.key && (
                    <motion.span
                      layoutId="mode-pill"
                      className="absolute inset-0 rounded-full"
                      style={{ background: opt.accent }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                  <opt.icon className="relative z-10 h-3.5 w-3.5" />
                  <span className="relative z-10">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div
            data-testid="interactive-app-preview"
            className="overflow-hidden rounded-3xl border border-white/10 bg-[#111318] p-8 sm:p-12"
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={mode}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="flex flex-wrap items-end justify-between gap-6">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      {m.statLabel}
                    </p>
                    <p className="mt-2 text-5xl font-extrabold tracking-tight text-slate-50">
                      {m.stat}
                      <span className="ml-2 text-base font-medium text-neutral-500">{m.unit}</span>
                    </p>
                    <p className="mt-1 text-sm text-neutral-400">{m.goal}</p>
                  </div>
                  <svg viewBox="0 0 80 80" className="h-20 w-20 -rotate-90">
                    <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7" />
                    <motion.circle
                      cx="40"
                      cy="40"
                      r="34"
                      fill="none"
                      stroke={m.accent}
                      strokeWidth="7"
                      strokeLinecap="round"
                      strokeDasharray="213.6"
                      initial={{ strokeDashoffset: 213.6 }}
                      animate={{ strokeDashoffset: 213.6 * (1 - 0.72) }}
                      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </svg>
                </div>

                <div className="mt-10 flex h-40 items-end justify-between gap-3">
                  {m.days.map((v, i) => (
                    <div key={`${mode}-${i}`} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: `${v}%` }}
                        transition={{ duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                        className="w-full rounded-t-lg"
                        style={{ background: m.accent, opacity: 0.35 + (v / 100) * 0.6 }}
                      />
                      <span className="font-mono text-[9px] uppercase tracking-widest text-neutral-600">
                        {DAYS[i]}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
